import type { MapMarker, MapMarkerCircle } from '../components/MapDisplay';

export interface MapData {
  center: [number, number];
  zoom?: number;
  markers: MapMarker[];
}

export interface MapDisplayData extends MapData {
  title?: string;
}

export interface MapLabelLocalizer {
  speedLimitLookup: (coordinates: string) => string;
  speedZone: (speed: string | number, unit: string, limit: string | number, difference: string | number) => string;
  speedLimit: (speed: string | number, unit: string, limit: string | number, difference: string | number) => string;
  incident: string;
}

type MapTranslator = (key: string, values?: Record<string, string | number>) => string;

export const createMapLabelLocalizer = (translate: MapTranslator): MapLabelLocalizer => ({
  speedLimitLookup: (coordinates) => translate('map.speedLimitLookup', { coordinates }),
  speedZone: (speed, unit, limit, difference) => translate('map.speedZone', {
    speed,
    unit,
    limit,
    difference,
  }),
  speedLimit: (speed, unit, limit, difference) => translate('map.speedLimit', {
    speed,
    unit,
    limit,
    difference,
  }),
  incident: translate('map.incident'),
});

const defaultMapLabelLocalizer: MapLabelLocalizer = {
  speedLimitLookup: (coordinates) => `Speed limit lookup (${coordinates})`,
  speedZone: (speed, unit, limit, difference) => `${speed} ${unit} in ${limit} ${unit} zone (+${difference})`,
  speedLimit: (speed, unit, limit, difference) => `${speed} ${unit} (limit ${limit}, +${difference})`,
  incident: 'Incident',
};

type MapDataExtractor = (input: Record<string, any>, labels: MapLabelLocalizer) => MapData | null;
type MapResultExtractor = (geoData: Record<string, any>, labels: MapLabelLocalizer) => MapData | null;

function finiteCoordinatePair(value: unknown): [number, number] | null {
  if (!Array.isArray(value) || value.length < 2) return null;
  const lat = Number(value[0]);
  const lon = Number(value[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  return [lat, lon];
}

function finiteNumber(value: unknown): number | null {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function finitePositiveNumber(value: unknown): number | null {
  const num = finiteNumber(value);
  return num !== null && num > 0 ? num : null;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function normalizeCircle(
  rawCircle: unknown,
): MapMarkerCircle | undefined {
  if (!rawCircle || typeof rawCircle !== 'object') return undefined;

  const circle = rawCircle as Record<string, any>;
  const radiusMeters = finitePositiveNumber(circle.radiusMeters);

  if (radiusMeters === null) return undefined;

  const normalized: MapMarkerCircle = {
    radiusMeters,
  };

  const color = optionalString(circle.color);
  const fillColor = optionalString(circle.fillColor);
  const weight = finitePositiveNumber(circle.weight);
  const opacity = finiteNumber(circle.opacity);
  const fillOpacity = finiteNumber(circle.fillOpacity);

  if (color) normalized.color = color;
  if (fillColor) normalized.fillColor = fillColor;
  if (weight !== null) normalized.weight = weight;
  if (opacity !== null) normalized.opacity = opacity;
  if (fillOpacity !== null) normalized.fillOpacity = fillOpacity;

  return normalized;
}

function normalizeMarker(marker: Record<string, any>): MapMarker | null {
  const position = finiteCoordinatePair(marker.position);
  if (!position) return null;
  return {
    position,
    label: typeof marker.label === 'string' ? marker.label : undefined,
    color: typeof marker.color === 'string' ? marker.color : undefined,
    circle: normalizeCircle(marker.circle),
    data: marker.data && typeof marker.data === 'object' ? marker.data : undefined,
  };
}

function mapDataFromSessionDoc(
  event: Record<string, any>,
  labels: MapLabelLocalizer,
): MapData | null {
  if (event.docType && event.docType !== 'map') return null;

  const data = event.data;
  if (!data || typeof data !== 'object') return null;

  if (Array.isArray(data.center) && Array.isArray(data.markers)) {
    const center = finiteCoordinatePair(data.center);
    if (!center) return null;

    const markers = data.markers
      .filter((marker: unknown): marker is Record<string, any> => Boolean(marker && typeof marker === 'object'))
      .map((marker: Record<string, any>) => normalizeMarker(marker))
      .filter(Boolean) as MapMarker[];

    return {
      center,
      zoom: typeof data.zoom === 'number' ? data.zoom : 12,
      markers,
    };
  }

  const incidents = Array.isArray(data.incidents) ? data.incidents : [];
  const markers = incidents
    .map((incident: Record<string, any>) => {
      const lat = Number(incident.lat ?? incident.latitude);
      const lon = Number(incident.lon ?? incident.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
      const speed = incident.speed ?? '';
      const limit = incident.speed_limit ?? '';
      const diff = incident.difference ?? '';
      const unit = incident.unit ?? 'mph';
      const label = [
        incident.timestamp,
        incident.location,
        speed !== '' && limit !== '' && diff !== ''
          ? labels.speedZone(speed, unit, limit, diff)
          : null,
      ].filter(Boolean).join('\n');

      return {
        position: [lat, lon] as [number, number],
        label: label || labels.incident,
        color: Number(diff) >= 20 ? 'red' : Number(diff) >= 10 ? 'orange' : 'yellow',
        data: incident,
      };
    })
    .filter(Boolean) as MapMarker[];

  if (markers.length === 0) return null;
  return {
    center: markers[0].position,
    zoom: 11,
    markers,
  };
}

/**
 * Registry mapping tool names to functions that extract map display data
 * from the tool's input parameters. Add new entries here to support
 * map rendering for additional tools.
 */
const toolExtractors: Record<string, MapDataExtractor> = {
  speed_limit_lookup: (input, labels) => {
    const lat = parseFloat(input.latitude);
    const lon = parseFloat(input.longitude);
    if (isNaN(lat) || isNaN(lon)) return null;
    return {
      center: [lat, lon],
      zoom: 16,
      markers: [{
        position: [lat, lon],
        label: labels.speedLimitLookup(`${lat.toFixed(4)}, ${lon.toFixed(4)}`),
      }],
    };
  },
};

/**
 * Registry mapping tool names to functions that extract map display data
 * from tool result geo data (for tools where coordinates come from the result,
 * not the input).
 */
const resultExtractors: Record<string, MapResultExtractor> = {
  get_driver_incidents: (geoData, labels) => {
    const incidents = geoData.incidents;
    if (!Array.isArray(incidents) || incidents.length === 0) return null;

    const markers: MapMarker[] = [];
    let sumLat = 0;
    let sumLon = 0;

    for (const inc of incidents) {
      const lat = parseFloat(inc.lat);
      const lon = parseFloat(inc.lon);
      if (isNaN(lat) || isNaN(lon)) continue;

      sumLat += lat;
      sumLon += lon;

      const speed = inc.speed ?? '';
      const limit = inc.speed_limit ?? '';
      const diff = inc.difference ?? '';
      const unit = inc.unit ?? 'mph';
      const location = inc.location ?? '';
      const time = inc.timestamp ?? '';

      const label = [
        time,
        location ? `${location}` : '',
        labels.speedLimit(speed, unit, limit, diff),
      ].filter(Boolean).join('\n');

      markers.push({ position: [lat, lon], label });
    }

    if (markers.length === 0) return null;

    const centerLat = sumLat / markers.length;
    const centerLon = sumLon / markers.length;

    // Zoom level based on marker spread
    let zoom = 12;
    if (markers.length === 1) {
      zoom = 15;
    } else {
      const lats = markers.map((m) => m.position[0]);
      const lons = markers.map((m) => m.position[1]);
      const latSpread = Math.max(...lats) - Math.min(...lats);
      const lonSpread = Math.max(...lons) - Math.min(...lons);
      const maxSpread = Math.max(latSpread, lonSpread);
      if (maxSpread < 0.01) zoom = 15;
      else if (maxSpread < 0.05) zoom = 13;
      else if (maxSpread < 0.2) zoom = 11;
      else if (maxSpread < 1) zoom = 9;
      else zoom = 7;
    }

    return {
      center: [centerLat, centerLon],
      zoom,
      markers,
    };
  },
};

/**
 * Scan stream events for tool_use and tool_result events that have registered extractors.
 * Checks tool_result events first (result-based geo data), then tool_use events (input-based).
 * Returns the first match (or null if no map-capable tool was used).
 */
export function extractMapDataFromEvents(
  streamEvents: Array<Record<string, any>>,
  labels: MapLabelLocalizer = defaultMapLabelLocalizer,
): MapData | null {
  const mapDisplayData = extractMapDisplayDataFromEvents(streamEvents, labels);
  if (!mapDisplayData) return null;

  const { title, ...mapData } = mapDisplayData;
  return mapData;
}

/**
 * Extract map data plus a human-readable title for static export contexts.
 */
export function extractMapDisplayDataFromEvents(
  streamEvents: Array<Record<string, any>>,
  labels: MapLabelLocalizer = defaultMapLabelLocalizer,
): MapDisplayData | null {
  // Check tool_result events first (contain actual geo data from tool output)
  for (const event of streamEvents) {
    if (event.type !== 'tool_result' || !event.toolName) continue;
    const extractor = resultExtractors[event.toolName];
    if (!extractor) continue;
    const data = extractor(event.geoData || {}, labels);
    if (data) return { ...data, title: optionalString(event.label) || optionalString(event.toolName) };
  }

  // Fall back to tool_use events (geo data from tool input)
  for (const event of streamEvents) {
    if (event.type === 'session_doc') {
      const docMapData = mapDataFromSessionDoc(event, labels);
      if (docMapData) return { ...docMapData, title: optionalString(event.label) };
    }

    if (event.type !== 'tool_use' || !event.toolName) continue;
    const extractor = toolExtractors[event.toolName];
    if (!extractor) continue;
    const data = extractor(event.input || {}, labels);
    if (data) return { ...data, title: optionalString(event.label) || optionalString(event.toolName) };
  }

  return null;
}
